/**
 * MOMAX Home Scroll V3 — desktop easing with bounded main-thread work.
 * Keeps ScrollTrigger protected scenes and original wheel distance/easing.
 * Change ENABLE_DESKTOP_EASING to false for native/compositor scrolling.
 */
(() => {
  'use strict';

  const ENABLE_DESKTOP_EASING = true;
  const DECAY_MS = 110;
  const LOCK_CHECK_MS = 120;
  const RANGE_CACHE_MS = 250;
  const MAX_CACHE_MS = 500;
  const protectedSelector = 'momax-scroll-story';
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const fineDesktop = matchMedia('(min-width: 1200px) and (hover: hover) and (pointer: fine)');

  if (!ENABLE_DESKTOP_EASING) return;

  let frame = 0;
  let target = window.scrollY;
  let written = window.scrollY;
  let previousTime = 0;
  let lastLockCheck = -Infinity;
  let lastRangeCheck = -Infinity;
  let lastMaxCheck = -Infinity;
  let scrollLocked = false;
  let maxScroll = 0;
  let ranges = [];

  function maximum(force = false) {
    const now = performance.now();
    if (force || now - lastMaxCheck > MAX_CACHE_MS) {
      const root = document.scrollingElement || document.documentElement;
      maxScroll = Math.max(0, root.scrollHeight - window.innerHeight);
      lastMaxCheck = now;
    }
    return maxScroll;
  }

  function locked(force = false) {
    const now = performance.now();
    if (force || now - lastLockCheck > LOCK_CHECK_MS) {
      const body = getComputedStyle(document.body);
      const root = getComputedStyle(document.documentElement);
      scrollLocked = body.position === 'fixed' ||
        /hidden|clip/.test(body.overflowY) ||
        /hidden|clip/.test(root.overflowY);
      lastLockCheck = now;
    }
    return scrollLocked;
  }

  function stop() {
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
    target = written = window.scrollY;
  }

  function write(top) {
    window.scrollTo({ top, left: window.scrollX, behavior: 'instant' });
    written = window.scrollY;
  }

  function finish() {
    if (frame && !locked(true)) write(Math.min(target, maximum(true)));
    stop();
  }

  function refreshRanges(force = false) {
    const now = performance.now();
    if (!force && now - lastRangeCheck < RANGE_CACHE_MS) return ranges;
    lastRangeCheck = now;
    const triggers = window.ScrollTrigger?.getAll?.() || [];
    ranges = triggers
      .filter(trigger => trigger.enabled !== false &&
        trigger.trigger instanceof Element &&
        trigger.trigger.closest(protectedSelector) &&
        Number.isFinite(trigger.start) && Number.isFinite(trigger.end) &&
        trigger.end > trigger.start)
      .map(trigger => ({start: Math.round(trigger.start), end: Math.round(trigger.end)}));
    return ranges;
  }

  function rangeAt(position, direction, candidates) {
    return candidates.find(range => direction > 0
      ? position >= range.start && position < range.end
      : position > range.start && position <= range.end);
  }

  function nextPosition(proposed) {
    if (!ranges.length) return proposed;
    const current = window.scrollY;
    const direction = Math.sign(target - current);
    const active = rangeAt(current, direction, ranges);
    if (active) return direction > 0 ? Math.min(target, active.end) : Math.max(target, active.start);
    return ranges.reduce((next, range) => {
      if (direction > 0 && range.start > current) return Math.min(next, range.start);
      if (direction < 0 && range.end < current) return Math.max(next, range.end);
      return next;
    }, proposed);
  }

  function localScroll(event) {
    // Keep native scrolling in drawers, inputs, dialogs and independently
    // scrollable areas. Computed style is read only for wheel gestures.
    return event.composedPath().some(element => {
      if (!(element instanceof Element) ||
        element === document.body || element === document.documentElement) return false;
      if (element.matches('input,textarea,select,[contenteditable],[contenteditable="true"],[role="dialog"],.drawer')) return true;
      if (element.scrollHeight <= element.clientHeight + 1) return false;
      return /auto|scroll/.test(getComputedStyle(element).overflowY);
    });
  }

  function tick(now) {
    // Read computed styles at most once every LOCK_CHECK_MS; don't force
    // scrollHeight or rebuild ScrollTrigger lists on every animation frame.
    if (!fineDesktop.matches || reduced.matches || locked() ||
        Math.abs(window.scrollY - written) > 1) {
      stop();
      return;
    }
    target = Math.min(target, maximum());
    const current = window.scrollY;
    const remaining = target - current;
    const elapsed = Math.max(0, now - previousTime);
    previousTime = now;
    if (Math.abs(remaining) <= 1) {
      write(target);
      stop();
      return;
    }
    const next = current + remaining * (1 - Math.exp(-elapsed / DECAY_MS));
    const proposed = Math.abs(next - current) < 1 ? current + Math.sign(remaining) : next;
    write(nextPosition(proposed));
    frame = requestAnimationFrame(tick);
  }

  function wheel(event) {
    if (!fineDesktop.matches || window.Shopify?.designMode ||
        event.defaultPrevented || !event.cancelable || reduced.matches || locked(true)) {
      if (frame) stop();
      return;
    }
    if (event.ctrlKey || event.metaKey || event.shiftKey ||
        event.deltaMode !== 0 || !event.deltaY ||
        Math.abs(event.deltaX) > Math.abs(event.deltaY) || localScroll(event)) {
      if (frame) finish();
      return;
    }

    if (frame && Math.abs(window.scrollY - written) > 1) stop();
    const destination = Math.max(0, Math.min(maximum(),
      (frame ? target : window.scrollY) + event.deltaY));
    refreshRanges();
    const active = rangeAt(window.scrollY, Math.sign(event.deltaY), ranges);
    if (!frame && active && destination >= active.start && destination <= active.end) return;

    event.preventDefault();
    target = destination;
    if (!frame) {
      written = window.scrollY;
      previousTime = performance.now();
      frame = requestAnimationFrame(tick);
    }
  }

  window.addEventListener('wheel', wheel, {passive: false});
  window.addEventListener('pointerdown', stop, {passive: true});
  window.addEventListener('touchstart', stop, {passive: true});
  window.addEventListener('keydown', () => { if (frame) finish(); });
  window.addEventListener('resize', () => { stop(); lastMaxCheck = -Infinity; lastRangeCheck = -Infinity; });
  window.addEventListener('pagehide', stop);
  window.addEventListener('pageshow', stop);
  document.addEventListener('visibilitychange', () => { if (document.hidden) stop(); });
  reduced.addEventListener('change', () => { if (frame) finish(); });
  fineDesktop.addEventListener('change', stop);
  // Refresh cached scene coordinates on explicit GSAP refresh when available.
  // During wheel gestures, periodic refreshRanges() remains the fallback.
  window.addEventListener('momax:scrolltrigger:refresh', () => { lastRangeCheck = -Infinity; });
})();
